import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, AlertCircle, CalendarClock, Tag } from 'lucide-react'
import { format, startOfMonth, endOfMonth, addMonths, getDate, getDay } from 'date-fns'
import { it } from 'date-fns/locale'
import { getRateCalendarGrid, type RateCalDay } from '../../lib/api/client'
import { RateCalendarDayModal } from './RateCalendarDayModal'
import { cn } from '../../lib/cn'

const DOW = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="cv-panel px-4 py-3.5">
      <div className="text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{label}</div>
      <div className={cn('mt-1 font-display text-[24px] font-bold leading-none tracking-[-.5px]', tone ?? 'text-coal')}>{value}</div>
      {sub && <div className="mt-1 text-[12px] text-muted">{sub}</div>}
    </div>
  )
}

export function RateCalendarPage() {
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()))
  const [modalDate, setModalDate] = useState<string | null>(null)

  const monthStart = anchor
  const monthEnd = endOfMonth(anchor)
  const startStr = format(monthStart, 'yyyy-MM-dd')
  const endStr = format(monthEnd, 'yyyy-MM-dd')
  const todayStr = format(new Date(), 'yyyy-MM-dd')

  const grid = useQuery({
    queryKey: ['rateCalendarGrid', startStr, endStr],
    queryFn: () => getRateCalendarGrid(startStr, endStr),
    placeholderData: keepPreviousData,
  })
  const byDay = useMemo(() => {
    const m: Record<string, RateCalDay> = {}
    for (const d of grid.data ?? []) m[d.date] = d
    return m
  }, [grid.data])

  const dStr = (d: number) => format(new Date(anchor.getFullYear(), anchor.getMonth(), d), 'yyyy-MM-dd')
  const daysInMonth = getDate(monthEnd)
  const lead = (getDay(monthStart) + 6) % 7
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth])

  const stats = useMemo(() => {
    let specialDays = 0
    let specials = 0
    for (const d of days) {
      const day = byDay[dStr(d)]
      if (!day) continue
      const ov = day.transports.reduce((s, t) => s + t.overrideCount, 0)
      if (ov > 0) specialDays++
      specials += ov
    }
    return { specialDays, specials }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byDay, anchor, days])

  const monthLabel = cap1(format(anchor, 'LLLL yyyy', { locale: it }))
  const isCurrentMonth = startStr === format(startOfMonth(new Date()), 'yyyy-MM-dd')

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
            Calendario tariffe
          </h2>
          <p className="mt-1.5 max-w-[70ch] text-[13.5px] text-muted">
            Prezzi speciali per giorni specifici — eventi veneziani (Biennale, Carnevale, Redentore), weekend, ponti.
            Clicca un giorno per impostare le tariffe speciali.
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2.5 sm:ml-auto sm:w-auto">
          <div className="ml-auto flex items-center gap-1 rounded-lg border border-line bg-card p-1 sm:ml-0">
            <button onClick={() => setAnchor((a) => addMonths(a, -1))} className="rounded-md px-1.5 py-1 text-muted hover:bg-paper hover:text-coal" title="Mese precedente">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[120px] px-1 text-center text-[13px] font-semibold text-coal">{monthLabel}</span>
            <button onClick={() => setAnchor((a) => addMonths(a, 1))} className="rounded-md px-1.5 py-1 text-muted hover:bg-paper hover:text-coal" title="Mese successivo">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {!isCurrentMonth && (
            <button onClick={() => setAnchor(startOfMonth(new Date()))} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-3 py-[9px] text-[13px] font-semibold text-slate hover:border-faint">
              <CalendarClock className="h-4 w-4" /> Oggi
            </button>
          )}
        </div>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-2 gap-[14px] lg:grid-cols-4">
        <Stat label="Giorni con speciali" value={String(stats.specialDays)} sub="nel mese" tone={stats.specialDays > 0 ? 'text-oro-700' : 'text-coal'} />
        <Stat label="Tariffe speciali" value={String(stats.specials)} sub="override attivi" />
        <Stat label="Mese" value={monthLabel.split(' ')[0]} sub={monthLabel.split(' ')[1]} />
        <Stat label="Listino base" value="Tariffe" sub="gestione listini →" />
      </div>

      {/* calendar */}
      <div className="cv-panel p-4 sm:p-5">
        {grid.isError ? (
          <div className="px-4 py-16 text-center text-sm text-muted">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il calendario tariffe.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-7 gap-1.5">
                {DOW.map((d) => (
                  <div key={d} className="pb-1 text-center text-[11px] font-bold uppercase tracking-[1px] text-muted">
                    {d}
                  </div>
                ))}
                {Array.from({ length: lead }).map((_, i) => (
                  <div key={`lead-${i}`} className="min-h-[112px] rounded-lg bg-paper/40" />
                ))}
                {days.map((d) => {
                  const ds = dStr(d)
                  const day = byDay[ds]
                  const isToday = ds === todayStr
                  const hasSpecial = day ? day.transports.some((t) => t.overrideCount > 0) : false
                  return (
                    <button
                      key={d}
                      onClick={() => setModalDate(ds)}
                      className={cn(
                        'flex min-h-[112px] flex-col overflow-hidden rounded-lg border text-left transition-colors hover:border-faint',
                        hasSpecial ? 'border-[#f0e0b0] bg-oro-soft' : 'border-line bg-card',
                      )}
                    >
                      <div className={cn('flex items-center justify-between px-2.5 py-1.5', isToday ? 'bg-coal text-white' : 'bg-black/[0.03] text-coal')}>
                        {isToday ? <span className="text-[10px] font-bold uppercase tracking-wider">oggi</span> : <span />}
                        <span className="text-[15px] font-bold">{d}</span>
                      </div>
                      {grid.isLoading ? (
                        <div className="flex-1 p-2.5"><div className="h-3.5 w-16 rounded bg-line-soft" /></div>
                      ) : !day ? (
                        <div className="flex flex-1 items-center justify-center text-[11px] text-faint">—</div>
                      ) : (
                        <div className="flex flex-1 flex-col gap-1 p-2.5">
                          {day.transports.map((t) => (
                            <div key={t.label} className="flex items-center justify-between gap-1.5 text-[12px]">
                              <span className="truncate text-slate">{t.label}</span>
                              <span className="flex shrink-0 items-center gap-1">
                                {t.count - t.overrideCount > 0 && (
                                  <span className="rounded-full bg-paper px-1.5 text-[10.5px] font-semibold tnum text-muted ring-1 ring-line">
                                    {t.count - t.overrideCount}
                                  </span>
                                )}
                                {t.overrideCount > 0 && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-oro px-1.5 text-[10.5px] font-bold tnum text-white">
                                    <Tag className="h-2.5 w-2.5" />
                                    {t.overrideCount}
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* legend */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted">
          <span className="flex items-center gap-1.5">
            <span className="rounded-full bg-paper px-1.5 text-[10.5px] font-semibold text-muted ring-1 ring-line">N</span> tariffe a listino
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-0.5 rounded-full bg-oro px-1.5 text-[10.5px] font-bold text-white">
              <Tag className="h-2.5 w-2.5" /> N
            </span>
            prezzi speciali
          </span>
          <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-[3px] border border-[#f0e0b0] bg-oro-soft" /> giorno con tariffe speciali</span>
        </div>
      </div>

      <RateCalendarDayModal date={modalDate} onClose={() => setModalDate(null)} onNavigate={(d) => setModalDate(d)} />
    </div>
  )
}
